import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mail, FileText, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

const PLACEHOLDERS = ["first_name", "last_name", "full_name", "property_address", "email", "company_name"];

export default function OutreachPage() {
  const { user } = useAuth();
  const isPMAdmin = user?.role === "property_manager" && user?.account_role === "admin";
  const isStaff = user?.role === "ntn_staff";
  const canEditBody = isPMAdmin || isStaff;

  const [emailBody, setEmailBody] = useState("");
  const [savedBody, setSavedBody] = useState("");
  const [hasLink, setHasLink] = useState(false);
  const [savingBody, setSavingBody] = useState(false);

  const [form, setForm] = useState({ first_name: "", last_name: "", property_address: "", email: "" });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadAll = async () => {
    try {
      const [s, h] = await Promise.all([
        apiClient.get("/outreach/settings"),
        apiClient.get("/outreach/history"),
      ]);
      setEmailBody(s.data.email_body || "");
      setSavedBody(s.data.email_body || "");
      setHasLink(!!s.data.has_link);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load settings");
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const saveBody = async () => {
    setSavingBody(true);
    try {
      const { data } = await apiClient.patch("/outreach/settings", { email_body: emailBody });
      setSavedBody(data.email_body || "");
      toast.success("Email template saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setSavingBody(false); }
  };

  const send = async () => {
    setSending(true);
    try {
      await apiClient.post("/outreach/send", form);
      toast.success(`Application link sent to ${form.email}`);
      setForm({ first_name: "", last_name: "", property_address: "", email: "" });
      const { data } = await apiClient.get("/outreach/history");
      setHistory(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };

  const formValid = form.first_name && form.last_name && form.property_address && form.email;
  const canSend = formValid && hasLink;

  return (
    <AppShell>
      <div className="mb-8">
        <div className="font-overline text-[#4A5568] mb-3 flex items-center gap-2">
          <Mail className="h-3.5 w-3.5" /> Send Application Link
        </div>
        <h1 className="font-serif text-4xl text-[#0A192F]">Send Application Link</h1>
        <p className="text-sm text-[#4A5568] mt-2 max-w-2xl">
          Email the saved application link to a prospective applicant in seconds. NTN staff configures the link on your account; you customize the message.
        </p>
      </div>

      {/* Link status banner */}
      {!hasLink && (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-[#92400E] rounded-sm p-4 mb-6 flex items-start gap-3"
             data-testid="outreach-no-link-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="text-sm">
            <strong>No application link configured yet.</strong>
            <div className="text-xs mt-1">Your NTN account manager needs to set up your application link before you can send. Please contact them to get started.</div>
          </div>
        </div>
      )}
      {hasLink && (
        <div className="bg-[#22C55E]/8 border border-[#22C55E]/30 text-[#15803D] rounded-sm p-3 mb-6 flex items-center gap-2"
             data-testid="outreach-link-ready">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-sm">Your application link is set up and ready to send.</span>
        </div>
      )}

      {/* Boilerplate email body */}
      <section className="bg-white border border-[#0A192F]/10 rounded-sm p-6 mb-6">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="font-overline text-[#4A5568] mb-1 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" /> Email template
            </div>
            <p className="text-xs text-[#4A5568] max-w-xl">
              Customize the message your applicants receive with the application link.
              {canEditBody ? " Changes apply to everyone in your company." : " Only company admins can edit this template."}
            </p>
          </div>
          {canEditBody && (
            <Button onClick={saveBody} disabled={savingBody || emailBody === savedBody}
              className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-10" data-testid="outreach-save-body">
              <Save className="h-4 w-4 mr-2" /> {savingBody ? "Saving…" : "Save template"}
            </Button>
          )}
        </div>
        <Textarea
          rows={10}
          value={emailBody}
          onChange={(e) => setEmailBody(e.target.value)}
          disabled={!canEditBody}
          className="rounded-sm font-mono text-sm"
          data-testid="outreach-body-input"
        />
        <div className="mt-3 text-[11px] text-[#4A5568]">
          <strong className="font-mono tracking-[0.12em] uppercase text-[10px]">Placeholders:</strong>{" "}
          {PLACEHOLDERS.map((p) => (
            <code key={p} className="ml-1 px-1.5 py-0.5 bg-[#F3EFEA] rounded-sm font-mono text-[10px]">{`{{${p}}}`}</code>
          ))}
          <span className="ml-2">— the application link is appended automatically as a button at the bottom.</span>
        </div>
      </section>

      {/* Send form */}
      <section className="bg-white border border-[#0A192F]/10 rounded-sm p-6 mb-6">
        <h2 className="font-serif text-2xl text-[#0A192F] mb-4">Send to an applicant</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name">
            <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="h-11 rounded-sm" data-testid="outreach-first-name" />
          </Field>
          <Field label="Last name">
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="h-11 rounded-sm" data-testid="outreach-last-name" />
          </Field>
          <Field label="Property address" className="sm:col-span-2">
            <Input value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })}
              className="h-11 rounded-sm" data-testid="outreach-property" />
          </Field>
          <Field label="Applicant email" className="sm:col-span-2">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-11 rounded-sm" data-testid="outreach-email" />
          </Field>
        </div>
        <div className="mt-6 pt-4 border-t border-[#0A192F]/10 flex justify-end">
          <Button onClick={send} disabled={!canSend || sending}
            className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-6" data-testid="outreach-send-btn">
            <Send className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Send application link"}
          </Button>
        </div>
      </section>

      {/* History */}
      <section className="bg-white border border-[#0A192F]/10 rounded-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-[#0A192F]/10 flex items-center justify-between">
          <h2 className="font-serif text-xl text-[#0A192F]">Sent Application Links</h2>
          <div className="text-xs font-mono tracking-[0.14em] uppercase text-[#4A5568]">{history.length} {history.length === 1 ? "entry" : "entries"}</div>
        </div>
        {history.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-[#4A5568]">No application links sent yet.</div>
        ) : (
          <ul className="divide-y divide-[#0A192F]/8" data-testid="outreach-history-list">
            {history.map((h) => (
              <li key={h.id} className="px-5 py-3 flex items-center gap-4" data-testid={`outreach-history-${h.id}`}>
                <div className={`h-8 w-8 rounded-sm flex items-center justify-center shrink-0 ${
                  h.success ? "bg-[#22C55E]/10 text-[#15803D]" : "bg-[#EF4444]/10 text-[#B91C1C]"
                }`}>
                  {h.success ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0A192F] truncate">
                    <span className="font-medium">{h.full_name}</span>
                    <span className="text-[#4A5568] font-mono"> · {h.email}</span>
                  </div>
                  <div className="text-xs text-[#4A5568] truncate">{h.property_address}</div>
                  {h.error && <div className="text-[11px] text-[#B91C1C] mt-0.5">{h.error}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-[#4A5568]">
                    {fmtDateTime(h.sent_at)}
                  </div>
                  <div className="text-[10px] text-[#4A5568]">by {h.sent_by_name}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </AppShell>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
